const COLOR_SCHEME = {
    // To calculate contrast
    YIQ: {
        red: 0.299,
        green: 0.587,
        blue: 0.114,
    },

    DEFAULT_COLOR: "#0099ff",
    CONTRAST_HIGH: 125, // of 255
    CONTRAST_OVER: 220, // of 255
    LIGHTER: 1.3,
    DARKER: 0.7,
};

function PaletteFromHex(
    initial = COLOR_SCHEME.DEFAULT_COLOR,
    contrastHigh = COLOR_SCHEME.CONTRAST_HIGH,
    lighterLevel = COLOR_SCHEME.LIGHTER,
    darkerLevel = COLOR_SCHEME.DARKER
) {
    const lighter = getLighter(initial, lighterLevel);
    const darker = getDarker(initial, darkerLevel);

    return {
        ...colorOut("lighter", lighter, { contrastHigh }),
        ...colorOut("initial", initial, { contrastHigh }),
        ...colorOut("darker", darker, { contrastHigh }),
    };
}

/**
 *
 * @param {String} key prefix
 * @param {String} color hex
 * @param {Object} data contrastHigh
 * @returns {Object} { prefix || Color, prefixContrastOver, prefixRGB, prefixText, prefixTextIsLight, prefixTextInvert }
 */
function colorOut(key = "", color, { contrastHigh, contrastOver }) {
    contrastHigh = contrastHigh ?? COLOR_SCHEME.CONTRAST_HIGH;
    contrastOver = contrastOver ?? COLOR_SCHEME.CONTRAST_OVER;

    const { c } = parseHex(color);
    const textColor = c > contrastHigh ? "#000000" : "#ffffff";

    return {
        [key || "Color"]: color,
        [`${key}ContrastOver`]: c >= contrastOver,
        [`${key}RGB`]: hexToRgb(color),
        [`${key}Text`]: textColor,
        [`${key}TextIsLight`]: parseHex(textColor).c > contrastHigh,
        [`${key}TextInvert`]: invertHex(textColor),
    };
}

function parseHex(hex = "") {
    hex = hex.replace(/[^\da-f]+/gi, "");

    if (hex.length === 3) {
        hex = [hex[0], hex[0], hex[1], hex[1], hex[2], hex[2]].join("");
    }

    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // https://en.wikipedia.org/wiki/YIQ
    const c = r * COLOR_SCHEME.YIQ.red + g * COLOR_SCHEME.YIQ.green + b * COLOR_SCHEME.YIQ.blue;

    return { c, r, g, b };
}

function getLighter(hex, percent = COLOR_SCHEME.LIGHTER) {
    const { r, g, b } = parseHex(hex);

    return rgbToHex(toLighter(r, percent), toLighter(g, percent), toLighter(b, percent));
}

function getDarker(hex, percent = COLOR_SCHEME.DARKER) {
    const { r, g, b } = parseHex(hex);

    return rgbToHex(toDarker(r, percent), toDarker(g, percent), toDarker(b, percent));
}

function toLighter(dec, percent) {
    return Math.max(0, Math.min(255, parseInt(dec * percent)));
}

function toDarker(dec, percent) {
    return Math.max(0, Math.min(255, parseInt(dec * percent)));
}

function decToHex(dec) {
    return Math.round(dec).toString(16).padStart(2, "0");
}

function rgbToHex(red, green, blue) {
    return `#${decToHex(red)}${decToHex(green)}${decToHex(blue)}`;
}

function hexToRgb(hex) {
    const [_, r, g, b] = hex.match(/^\#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/);

    return {
        r: parseInt(r, 16),
        g: parseInt(g, 16),
        b: parseInt(b, 16),
    };
}

function hexToHsl(hex) {
    const { r, g, b } = hexToRgb(hex);
    return rgbToHsl(r, g, b);
}

function rgbToHsl(red, green, blue) {
    red /= 255;
    green /= 255;
    blue /= 255;

    const cmax = Math.max(red, green, blue);
    const cmin = Math.min(red, green, blue);
    const delta = cmax - cmin;

    let h = 0; // Hue
    let s = 0; // Saturation
    let l = (cmax + cmin) / 2; // Lightness

    if (delta) {
        /* if (l <= 0.5) {
            s = (max - min) / (max + min);
        } else {
            s = (max - min) / (2.0 - max - min);
        } */

        s = delta / (1 - Math.abs(2 * l - 1));

        if (red === cmax) {
            h = ((green - blue) / delta) % 6;
        } else if (green === cmax) {
            h = (blue - red) / delta + 2;
        } else {
            h = (red - green) / delta + 4;
        }

        h *= 60;
        h = h < 0 ? h + 360 : h;
    }

    s = Math.round(s * 10000) / 100;
    l = Math.round(l * 10000) / 100;

    return { h, s, l };
}

function hslToRgb(hue, saturation, lightness) {
    const h = hue - Math.floor(hue / 360) * 360;
    const s = saturation / 100;
    const l = lightness / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r, g, b;

    const dh = h / 60;
    if (dh < 1) {
        r = c;
        g = x;
        b = 0;
    } else if (dh < 2) {
        r = x;
        g = c;
        b = 0;
    } else if (dh < 3) {
        r = 0;
        g = c;
        b = x;
    } else if (dh < 4) {
        r = 0;
        g = x;
        b = c;
    } else if (dh < 5) {
        r = x;
        g = 0;
        b = c;
    } else if (dh < 6) {
        r = c;
        g = 0;
        b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return { r, g, b };
}

function hslToHex(hue, saturation, lightness) {
    const { r, g, b } = hslToRgb(hue, saturation, lightness);
    return rgbToHex(r, g, b);
}

function invertHex(hex) {
    let [_, r, g, b] = hex.match(/^\#?(\w{2})(\w{2})(\w{2})$/);

    r = 255 * (1 - parseInt(r, 16) / 255);
    g = 255 * (1 - parseInt(g, 16) / 255);
    b = 255 * (1 - parseInt(b, 16) / 255);

    return rgbToHex(r, g, b);
}

export {
    COLOR_SCHEME,
    PaletteFromHex,
    colorOut,
    parseHex,
    getLighter,
    getDarker,
    toLighter,
    toDarker,
    decToHex,
    rgbToHex,
    hexToRgb,
    hexToHsl,
    rgbToHsl,
    hslToRgb,
    hslToHex,
    invertHex,
};
